// BASTION kit.
//   Bulwark Frame (passive): still on the ground for 0.6 s = braced: −30% damage
//     taken and double shield regen, shown as a faint amber hex shimmer.
//   Siege Field (signature): a 34×260 hex barrier ~150 px ahead (toward the
//     cursor) standing on the ground for 6 s. Enemy shots that enter it are
//     destroyed; player shots passing through gain +60% damage, +25% size, the
//     shield tag and an amber tint (once each). SIEGE_LOCK: +50% duration and
//     enemies inside are slowed. SIGNATURE_EVOLVED: enemies touching it take 30 dps.
//   Fortress Protocol (ultimate): anchor for 6 s (no movement, immune to
//     knockback, weapons keep firing) while artillery shells rain on the cursor.

import Phaser from 'phaser';
import { TEX } from '../../art/keys';
import { DEPTH } from '../../config/constants';
import { COLORS } from '../../config/palette';
import type { EnemyApi, ProjectileHandle } from '../../core/contracts';
import type { DamageInfo, StatusApplication } from '../../core/types';
import { discPoint, rectCircleOverlap, siegeFieldRect, SIEGE, type Rect } from './math';
import { ATEX, sfx, targetableList, texOr, type Kit, type KitHost, type TimerHandle } from './shared';

export const BASTION_TUNING = {
  braceTime: 0.6,
  braceIncoming: 0.7,
  braceShieldRegen: 2,
  fieldDuration: 6,
  siegeLockDurationMult: 1.5,
  fieldDamageMult: 1.6,
  fieldScaleMult: 1.25,
  /** SIEGE_LOCK slow applied to enemies inside the barrier. */
  siegeLockSlow: 0.5,
  /** SIGNATURE_EVOLVED contact damage per second. */
  evolvedDps: 30,
  fieldTick: 0.2,
  fortressDuration: 6,
  shellInterval: 0.22,
  shellSpread: 90,
  shellRadius: 120,
  shellDamage: 48,
  shellWarnMs: 350,
} as const;

/** Unique ids for touchedZones (kept far from combat's own zone ids). */
let nextFieldId = 0x51e6e000;

interface Field {
  id: number;
  rect: Rect;
  removeZone: () => void;
  until: number;
  duration: number;
  slowLock: boolean;
  evolved: boolean;
  tick: number;
  /** 0..1 brightness boost after blocking / amplifying a shot. */
  flash: number;
  age: number;
  ending: boolean;
  glow: Phaser.GameObjects.Image;
  hex: Phaser.GameObjects.TileSprite;
  frame: Phaser.GameObjects.Graphics;
  lastBlockSfx: number;
}

interface Fortress {
  until: number;
  shells: TimerHandle;
  anchor: Phaser.GameObjects.Graphics;
  ground: Phaser.GameObjects.Image;
  side: number;
}

export function createBastionKit(h: KitHost): Kit {
  return new BastionKit(h);
}

class BastionKit implements Kit {
  private fields: Field[] = [];
  private fortress: Fortress | null = null;
  private braced = false;
  private braceRefresh = 0;
  private shimmer: Phaser.GameObjects.Image | null = null;
  private shimmerAlpha = 0;
  private age = 0;

  constructor(private readonly h: KitHost) {}

  get signatureActive(): boolean {
    return this.fields.some((f) => !f.ending);
  }

  get ultimateActive(): boolean {
    return this.fortress !== null;
  }

  start(): void {
    const scene = this.h.ctx.scene;
    const key = texOr(scene, TEX.shield, ATEX.bubble);
    this.shimmer = this.h.track(scene.add.image(0, 0, key));
    this.shimmer.setTint(COLORS.bastion).setBlendMode(Phaser.BlendModes.ADD).setDepth(DEPTH.PLAYER + 1).setAlpha(0);
    this.shimmer.setDisplaySize(130, 150);
  }

  canSignature(): boolean {
    return true;
  }

  outgoing(_target: EnemyApi | null, _info: DamageInfo): number {
    return 1;
  }

  incoming(_info: DamageInfo): number {
    return this.braced ? BASTION_TUNING.braceIncoming : 1;
  }

  // ───────────────────────────── Siege Field ─────────────────────────────

  signature(): void {
    const { ctx, safe, timers } = this.h;
    const p = ctx.player;
    const T = BASTION_TUNING;
    const aim = ctx.input.aim(p.x, p.y);
    const dir: 1 | -1 = aim.x > p.x + 4 ? 1 : aim.x < p.x - 4 ? -1 : p.facing;

    // Do not sink into a wall between BASTION and the spot.
    const reach = SIEGE.offset + SIEGE.width / 2 + SIEGE.wallMargin;
    const wall = safe.call('world.raycast', () => ctx.world.raycast(p.x, p.y, p.x + dir * reach, p.y), null);
    const wallX = wall && wall.hit ? wall.x : null;
    const probe = siegeFieldRect(p.x, dir, 0, wallX);
    const cx = probe.x + probe.w / 2;
    // Stand on the first ground below the barrier's spot (fall back to BASTION's feet over pits).
    const ground = safe.call('world.groundBelow', () => ctx.world.groundBelow(cx, p.y - 30, 720), null) ?? p.feetY;
    const rect = siegeFieldRect(p.x, dir, ground, wallX);
    // Low ceilings cut the barrier short.
    const up = safe.call('world.raycast', () => ctx.world.raycast(cx, ground - 6, cx, ground - SIEGE.height), null);
    if (up && up.hit) {
      const hgt = Math.max(90, up.dist + 6);
      rect.y = ground - hgt;
      rect.h = hgt;
    }

    const slowLock = this.h.flag('SIEGE_LOCK');
    const duration = T.fieldDuration * (slowLock ? T.siegeLockDurationMult : 1);
    const field = this.buildField(rect, duration, slowLock, this.h.flag('SIGNATURE_EVOLVED'));
    field.until = timers.now + duration;
    this.fields.push(field);

    field.removeZone = safe.call(
      'projectiles.addZone',
      () =>
        ctx.projectiles.addZone({
          shape: { kind: 'rect', x: rect.x, y: rect.y, w: rect.w, h: rect.h },
          affects: 'both',
          onEnter: (pr) => this.onProjectile(field, pr),
        }),
      () => undefined,
    );

    sfx(ctx, safe, 'siege_field');
    safe.run('fx.sparks', () => ctx.fx.sparks(cx, ground, -Math.PI / 2, { count: 14, color: COLORS.bastionGlow, speed: 420, spread: 1.4 }));
    safe.run('fx.smoke', () => ctx.fx.smoke(cx, ground - 6, { count: 4, color: COLORS.steel3 }));
    safe.run('fx.shake', () => ctx.fx.shake(0.004, 110));
  }

  private onProjectile(f: Field, pr: ProjectileHandle): void {
    if (f.ending) return;
    const { ctx, safe } = this.h;
    if (pr.spec.owner === 'enemy') {
      if (pr.spec.destructible === false) return;
      const ang = Math.atan2(pr.vy, pr.vx) + Math.PI;
      safe.run('fx.impact', () => ctx.fx.impact(pr.x, pr.y, ang, 'shield', COLORS.bastion));
      pr.kill(false);
      f.flash = 1;
      if (this.h.timers.now - f.lastBlockSfx > 0.08) {
        f.lastBlockSfx = this.h.timers.now;
        sfx(ctx, safe, 'hit_shield', { volume: 0.45, pitch: 0.9 + Math.random() * 0.2 });
      }
      return;
    }
    // Player shot: amplify once per field.
    if (pr.touchedZones.has(f.id)) return;
    pr.touchedZones.add(f.id);
    pr.damage *= BASTION_TUNING.fieldDamageMult;
    pr.scale *= BASTION_TUNING.fieldScaleMult;
    // New arrays: weapon specs may share their tag list between shots.
    if (!pr.spec.tags.includes('shield')) pr.spec.tags = [...pr.spec.tags, 'shield'];
    pr.spec.tint = COLORS.bastionGlow;
    f.flash = Math.max(f.flash, 0.55);
    safe.run('fx.sparks', () =>
      ctx.fx.sparks(pr.x, pr.y, Math.atan2(pr.vy, pr.vx), { count: 4, color: COLORS.bastionGlow, speed: 260, spread: 0.6 }),
    );
  }

  private buildField(rect: Rect, duration: number, slowLock: boolean, evolved: boolean): Field {
    const scene = this.h.ctx.scene;
    const cx = rect.x + rect.w / 2;
    const bottom = rect.y + rect.h;
    const color = evolved ? COLORS.bastionGlow : COLORS.bastion;

    const glow = this.h.track(scene.add.image(cx, bottom, texOr(scene, TEX.soft, ATEX.soft)));
    glow.setOrigin(0.5, 1).setTint(COLORS.bastion).setBlendMode(Phaser.BlendModes.ADD).setDepth(DEPTH.FX - 1);
    glow.setDisplaySize(rect.w * 4.2, rect.h * 1.12).setAlpha(0.32);

    const hex = this.h.track(scene.add.tileSprite(cx, bottom, rect.w, rect.h, texOr(scene, TEX.hex, ATEX.hex)));
    hex.setOrigin(0.5, 1).setTint(COLORS.bastionGlow).setBlendMode(Phaser.BlendModes.ADD).setDepth(DEPTH.FX).setAlpha(0.5);

    // Frame: bright edges, top cap and a ground projector, drawn once relative to the bottom centre.
    const frame = this.h.track(scene.add.graphics({ x: cx, y: bottom }));
    frame.setDepth(DEPTH.FX + 1);
    const hw = rect.w / 2;
    frame.fillStyle(color, 0.12);
    frame.fillRect(-hw, -rect.h, rect.w, rect.h);
    frame.lineStyle(3, color, 0.95);
    frame.lineBetween(-hw, -rect.h, -hw, 0);
    frame.lineBetween(hw, -rect.h, hw, 0);
    frame.lineStyle(1.5, 0xffffff, 0.8);
    frame.lineBetween(-hw + 2, -rect.h + 4, -hw + 2, -4);
    frame.lineBetween(hw - 2, -rect.h + 4, hw - 2, -4);
    frame.fillStyle(color, 1);
    frame.fillRect(-hw - 4, -rect.h - 3, rect.w + 8, 4);
    // Ground projector block.
    frame.fillStyle(COLORS.steel1, 1);
    frame.fillRect(-hw - 10, -10, rect.w + 20, 10);
    frame.fillStyle(COLORS.steel3, 1);
    frame.fillRect(-hw - 10, -10, rect.w + 20, 2);
    frame.fillStyle(color, 1);
    frame.fillRect(-hw - 6, -6, 6, 3);
    frame.fillRect(hw, -6, 6, 3);
    frame.fillRect(-hw, -13, rect.w, 3);

    // Deploy: the barrier rises out of its projector.
    for (const obj of [glow, hex, frame]) {
      obj.scaleY = 0.02 * (obj === glow ? glow.scaleY : 1);
    }
    const glowScale = (rect.h * 1.12) / glow.height;
    scene.tweens.add({ targets: [hex, frame], scaleY: 1, duration: 200, ease: 'Back.Out' });
    scene.tweens.add({ targets: glow, scaleY: glowScale, duration: 200, ease: 'Quad.Out' });

    return {
      id: nextFieldId++,
      rect,
      removeZone: () => undefined,
      until: 0,
      duration,
      slowLock,
      evolved,
      tick: 0,
      flash: 0.8,
      age: 0,
      ending: false,
      glow,
      hex,
      frame,
      lastBlockSfx: -1,
    };
  }

  private updateField(f: Field, dt: number): void {
    const { ctx, safe, timers } = this.h;
    f.age += dt;
    const remaining = f.until - timers.now;
    if (!f.ending && remaining <= 0) {
      this.collapseField(f);
      return;
    }
    if (f.ending) return;

    // Animated honeycomb, brighter after hits, flickering out in the last 0.8 s.
    f.hex.tilePositionY -= dt * 26;
    f.flash = Math.max(0, f.flash - dt * 3.5);
    const flicker = remaining < 0.8 ? (Math.sin(timers.now * 60) > 0 ? 1 : 0.35) : 1;
    const shimmer = 0.42 + 0.08 * Math.sin(f.age * 7) + f.flash * 0.5;
    f.hex.setAlpha(Math.min(1, shimmer) * flicker);
    f.glow.setAlpha((0.28 + f.flash * 0.4) * flicker);
    f.frame.setAlpha(flicker);

    // Area effects (SIEGE_LOCK slow, evolved contact damage) on a fixed tick.
    if (!f.slowLock && !f.evolved) return;
    f.tick -= dt;
    if (f.tick > 0) return;
    f.tick += BASTION_TUNING.fieldTick;
    const pad = 8;
    const area: Rect = { x: f.rect.x - pad, y: f.rect.y, w: f.rect.w + pad * 2, h: f.rect.h };
    for (const e of targetableList(ctx, safe)) {
      if (!rectCircleOverlap(area, e.x, e.y, e.radius)) continue;
      if (f.slowLock) {
        const slow: StatusApplication = { id: 'slow', potency: BASTION_TUNING.siegeLockSlow, duration: BASTION_TUNING.fieldTick + 0.3 };
        safe.run('status.apply', () => ctx.status.apply(e, slow));
      }
      if (f.evolved) {
        const info: DamageInfo = {
          amount: BASTION_TUNING.evolvedDps * BASTION_TUNING.fieldTick,
          tags: ['shield', 'electric'],
          source: 'player',
          sourceX: f.rect.x + f.rect.w / 2,
          sourceY: e.y,
          isDot: true,
          noTrigger: true,
          label: 'siege_field',
        };
        safe.run('damage.hitEnemy', () => ctx.damage.hitEnemy(e, info));
        // One shock stack per second of contact.
        if (Math.floor(f.age) !== Math.floor(f.age - BASTION_TUNING.fieldTick)) {
          safe.run('status.apply', () => ctx.status.apply(e, { id: 'shock', stacks: 1, duration: 1.5 }));
        }
        safe.run('fx.sparks', () =>
          ctx.fx.sparks(e.x < f.rect.x ? f.rect.x : f.rect.x + f.rect.w, e.y, e.x < f.rect.x ? Math.PI : 0, { count: 2, color: COLORS.bastionGlow, speed: 200 }),
        );
      }
    }
  }

  private collapseField(f: Field): void {
    if (f.ending) return;
    f.ending = true;
    f.removeZone();
    const { ctx, safe } = this.h;
    const cx = f.rect.x + f.rect.w / 2;
    const bottom = f.rect.y + f.rect.h;
    safe.run('fx.sparks', () => ctx.fx.sparks(cx, bottom - f.rect.h / 2, -Math.PI / 2, { count: 10, color: COLORS.bastion, speed: 240, spread: Math.PI }));
    const scene = ctx.scene;
    scene.tweens.add({
      targets: [f.hex, f.frame, f.glow],
      scaleY: 0.02,
      alpha: 0,
      duration: 220,
      ease: 'Quad.In',
      onComplete: () => this.disposeField(f),
    });
  }

  private disposeField(f: Field): void {
    for (const obj of [f.glow, f.hex, f.frame]) {
      this.h.untrack(obj);
      obj.destroy();
    }
    this.fields = this.fields.filter((x) => x !== f);
  }

  // ───────────────────────────── Fortress Protocol ─────────────────────────────

  ultimate(): void {
    const { ctx, safe, timers } = this.h;
    const T = BASTION_TUNING;
    const p = ctx.player;
    const scene = ctx.scene;

    safe.run('player.addBuff', () =>
      p.addBuff(
        'fortress_protocol',
        [
          { stat: 'moveSpeed', op: 'mul', value: 0 },
          { stat: 'knockbackResist', op: 'add', value: 1 },
        ],
        T.fortressDuration,
      ),
    );

    // Anchor struts slam into the ground on both sides of the legs.
    const anchor = this.h.track(scene.add.graphics({ x: p.x, y: p.feetY }));
    anchor.setDepth(DEPTH.PLAYER + 1);
    for (const s of [-1, 1]) {
      anchor.fillStyle(COLORS.steel2, 1);
      anchor.fillTriangle(s * 14, -30, s * 22, -36, s * 44, 0);
      anchor.fillStyle(COLORS.steel4, 1);
      anchor.fillTriangle(s * 14, -30, s * 18, -33, s * 40, 0);
      anchor.fillStyle(COLORS.bastion, 1);
      anchor.fillRect(s * 44 - 7, -3, 14, 3);
      anchor.fillCircle(s * 18, -32, 3);
    }
    anchor.setScale(1, 0.1);
    scene.tweens.add({ targets: anchor, scaleY: 1, duration: 140, ease: 'Back.Out' });

    const ground = this.h.track(scene.add.image(p.x, p.feetY, texOr(scene, TEX.ring, ATEX.ring)));
    ground.setTint(COLORS.bastion).setBlendMode(Phaser.BlendModes.ADD).setDepth(DEPTH.ZONES).setScale(0.4, 0.1).setAlpha(0.9);
    scene.tweens.add({ targets: ground, scaleX: 1.5, scaleY: 0.32, duration: 260, ease: 'Quad.Out' });

    this.fortress = {
      until: timers.now + T.fortressDuration,
      shells: timers.every(T.shellInterval, Math.floor(T.fortressDuration / T.shellInterval), () => this.fireShell(), 0.15),
      anchor,
      ground,
      side: 1,
    };

    sfx(ctx, safe, 'fortress_protocol');
    safe.run('fx.shake', () => ctx.fx.shake(0.01, 260));
    safe.run('fx.smoke', () => ctx.fx.smoke(p.x, p.feetY - 4, { count: 8, color: COLORS.steel3, scale: 1.2 }));
    safe.run('fx.ring', () => ctx.fx.ring(p.x, p.feetY, 160, { color: COLORS.bastion, width: 5, fromRadius: 30 }));
  }

  private fireShell(): void {
    const fo = this.fortress;
    if (!fo) return;
    const { ctx, safe, timers } = this.h;
    const T = BASTION_TUNING;
    const p = ctx.player;
    const scene = ctx.scene;

    // Launch from alternating shoulders.
    fo.side = -fo.side;
    const shoulder = safe.call('view.attachWorld', () => p.view.attachWorld(fo.side > 0 ? 'shoulderF' : 'shoulderR'), {
      x: p.x - p.facing * 14,
      y: p.y - 34,
      rot: 0,
    });
    const up = -Math.PI / 2 + p.facing * 0.25;
    safe.run('fx.muzzleFlash', () => ctx.fx.muzzleFlash(shoulder.x, shoulder.y, up, COLORS.bastionGlow, 0.9));
    safe.run('fx.smoke', () => ctx.fx.smoke(shoulder.x, shoulder.y, { count: 1, color: COLORS.steel3, scale: 0.6 }));
    safe.run('fx.shake', () => ctx.fx.shake(0.002, 50));

    // Target: around the cursor (gameplay rng), at the cursor's height.
    const ptr = ctx.input.pointerWorld;
    const off = discPoint(ctx.rng.next(), ctx.rng.next(), T.shellSpread);
    const tx = ptr.x + off.x;
    const ty = ptr.y + off.y;
    const radius = T.shellRadius * safe.call('player.stats', () => p.stats.explosionRadius, 1);
    safe.run('fx.warnCircle', () => ctx.fx.warnCircle(tx, ty, radius, T.shellWarnMs, { color: COLORS.bastion }));

    // Incoming shell streak from above the view.
    const top = scene.cameras.main.worldView.y - 60;
    const shell = this.h.track(scene.add.image(tx - p.facing * 90, top, texOr(scene, TEX.projMortar, ATEX.streak)));
    shell.setTint(COLORS.bastionGlow).setBlendMode(Phaser.BlendModes.ADD).setDepth(DEPTH.PROJECTILES);
    shell.setRotation(Math.atan2(ty - top, p.facing * 90));
    scene.tweens.add({ targets: shell, x: tx, y: ty, duration: T.shellWarnMs - 20, ease: 'Quad.In' });

    timers.after(T.shellWarnMs / 1000, () => {
      this.h.untrack(shell);
      shell.destroy();
      const info: DamageInfo = {
        amount: T.shellDamage,
        tags: ['explosive'],
        source: 'player',
        sourceX: tx,
        sourceY: ty,
        knockback: 320,
        label: 'fortress_protocol',
      };
      safe.run('damage.explode', () => ctx.damage.explode(tx, ty, radius, info, { fx: true, color: COLORS.bastion }));
      sfx(ctx, safe, 'explosion_small', { volume: 0.5, pitch: 0.85 + Math.random() * 0.3 });
      safe.run('fx.shake', () => ctx.fx.shake(0.005, 90));
    });
  }

  private endFortress(): void {
    const fo = this.fortress;
    if (!fo) return;
    this.fortress = null;
    fo.shells.cancel();
    const scene = this.h.ctx.scene;
    for (const obj of [fo.anchor, fo.ground]) {
      if (!obj.scene) continue;
      scene.tweens.add({
        targets: obj,
        alpha: 0,
        scaleY: obj.scaleY * 0.2,
        duration: 200,
        onComplete: () => {
          this.h.untrack(obj);
          obj.destroy();
        },
      });
    }
    const p = this.h.ctx.player;
    this.h.safe.run('fx.smoke', () => this.h.ctx.fx.smoke(p.x, p.feetY - 4, { count: 4, color: COLORS.steel3 }));
  }

  // ───────────────────────────── frame ─────────────────────────────

  update(dt: number): void {
    const { ctx, safe, timers } = this.h;
    const p = ctx.player;
    this.age += dt;

    // Bulwark Frame brace.
    const alive = safe.call('player.alive', () => p.alive, false);
    const braced = alive && this.h.stillFor >= BASTION_TUNING.braceTime;
    if (braced && !this.braced) {
      safe.run('fx.ring', () => ctx.fx.ring(p.x, p.feetY - 4, 58, { color: COLORS.bastion, width: 2, fromRadius: 22 }));
      this.braceRefresh = 0;
    }
    this.braced = braced;
    if (braced) {
      this.braceRefresh -= dt;
      if (this.braceRefresh <= 0) {
        this.braceRefresh = 0.2;
        safe.run('player.addBuff', () =>
          p.addBuff('bulwark_brace', [{ stat: 'shieldRegen', op: 'mul', value: BASTION_TUNING.braceShieldRegen }], 0.3),
        );
      }
    }
    if (this.shimmer?.scene) {
      const target = braced ? 0.16 + 0.05 * Math.sin(this.age * 6) : 0;
      this.shimmerAlpha += (target - this.shimmerAlpha) * Math.min(1, dt * 10);
      this.shimmer.setPosition(p.x, p.y - 6).setAlpha(this.shimmerAlpha).setVisible(this.shimmerAlpha > 0.005);
    }

    for (const f of this.fields.slice()) this.updateField(f, dt);

    const fo = this.fortress;
    if (fo) {
      if (timers.now >= fo.until) this.endFortress();
      else {
        if (fo.anchor.scene) fo.anchor.setPosition(p.x, p.feetY);
        if (fo.ground.scene) fo.ground.setPosition(p.x, p.feetY).setAlpha(0.6 + 0.3 * Math.sin(this.age * 9));
      }
    }
  }

  interrupt(): void {
    this.endFortress();
    for (const f of this.fields) this.collapseField(f);
  }

  destroy(): void {
    this.endFortress();
    for (const f of this.fields) {
      f.ending = true;
      try {
        f.removeZone();
      } catch {
        // projectile system already gone
      }
    }
    this.fields = [];
  }
}
